// ============================================
// Camera2D - 2D Camera with transform and projection
// ============================================

import { Mat4, Vec2 } from '@engine/math';

/**
 * 2D Camera for viewport management
 */
export class Camera2D {
  // Position (world space)
  x = 0;
  y = 0;
  
  // Zoom level (1 = normal, 2 = 2x zoom in, 0.5 = 2x zoom out)
  zoom = 1;
  
  // Rotation in radians
  rotation = 0;
  
  // Viewport dimensions (pixels)
  viewportWidth = 1920;
  viewportHeight = 1080;
  
  // Cached matrices
  private viewMatrix = new Mat4();
  private projectionMatrix = new Mat4();
  private viewProjectionMatrix = new Mat4();
  private inverseViewProjection = new Mat4();
  private dirty = true;
  
  constructor(viewportWidth?: number, viewportHeight?: number) {
    if (viewportWidth !== undefined) this.viewportWidth = viewportWidth;
    if (viewportHeight !== undefined) this.viewportHeight = viewportHeight;
    this.updateMatrices();
  }
  
  /**
   * Set camera position
   */
  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.dirty = true;
  }
  
  /**
   * Move camera by delta
   */
  translate(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.dirty = true;
  }
  
  /**
   * Set zoom level
   */
  setZoom(zoom: number): void {
    this.zoom = Math.max(0.01, zoom);
    this.dirty = true;
  }
  
  /**
   * Multiply zoom by factor
   */
  zoomBy(factor: number): void {
    this.setZoom(this.zoom * factor);
  }
  
  /**
   * Zoom towards a world position
   */
  zoomTowards(worldX: number, worldY: number, factor: number): void {
    const prevZoom = this.zoom;
    this.setZoom(this.zoom * factor);
    
    // Adjust position to keep the target point stationary
    const zoomRatio = this.zoom / prevZoom;
    this.x = worldX + (this.x - worldX) * zoomRatio;
    this.y = worldY + (this.y - worldY) * zoomRatio;
    this.dirty = true;
  }
  
  /**
   * Set rotation in radians
   */
  setRotation(radians: number): void {
    this.rotation = radians;
    this.dirty = true;
  }
  
  /**
   * Set viewport size
   */
  setViewport(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.dirty = true;
  }
  
  /**
   * Get view matrix
   */
  getViewMatrix(out: Float32Array): Float32Array {
    this.updateMatrices();
    return this.viewMatrix.toFloat32Array(out);
  }
  
  /**
   * Get projection matrix
   */
  getProjectionMatrix(out: Float32Array): Float32Array {
    this.updateMatrices();
    return this.projectionMatrix.toFloat32Array(out);
  }
  
  /**
   * Get combined view-projection matrix
   */
  getViewProjection(out: Float32Array): Float32Array {
    this.updateMatrices();
    return this.viewProjectionMatrix.toFloat32Array(out);
  }

  /**
   * Get inverse view-projection matrix (for screen/UV → world reconstruction)
   */
  getInverseViewProjection(out: Float32Array): Float32Array {
    this.updateMatrices();
    return this.inverseViewProjection.toFloat32Array(out);
  }
  
  /**
   * Convert screen coordinates to world coordinates
   */
  screenToWorld(screenX: number, screenY: number, out: Vec2): Vec2 {
    this.updateMatrices();
    
    // Convert to normalized device coordinates (-1 to 1)
    const ndcX = (screenX / this.viewportWidth) * 2 - 1;
    const ndcY = 1 - (screenY / this.viewportHeight) * 2; // Flip Y
    
    // Transform by inverse view-projection
    const inv = this.inverseViewProjection;
    const x = inv.m00 * ndcX + inv.m04 * ndcY + inv.m12;
    const y = inv.m01 * ndcX + inv.m05 * ndcY + inv.m13;
    const w = inv.m03 * ndcX + inv.m07 * ndcY + inv.m15;
    
    out.x = x / w;
    out.y = y / w;
    return out;
  }
  
  /**
   * Convert world coordinates to screen coordinates
   */
  worldToScreen(worldX: number, worldY: number, out: Vec2): Vec2 {
    this.updateMatrices();
    
    // Transform by view-projection
    const vp = this.viewProjectionMatrix;
    const x = vp.m00 * worldX + vp.m04 * worldY + vp.m12;
    const y = vp.m01 * worldX + vp.m05 * worldY + vp.m13;
    const w = vp.m03 * worldX + vp.m07 * worldY + vp.m15;
    
    // Convert from NDC to screen coordinates
    out.x = ((x / w) + 1) * 0.5 * this.viewportWidth;
    out.y = (1 - (y / w)) * 0.5 * this.viewportHeight; // Flip Y
    return out;
  }
  
  /**
   * Get visible world bounds (AABB)
   */
  getVisibleBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    this.updateMatrices();
    
    const temp = new Vec2();
    
    // Transform viewport corners to world space
    this.screenToWorld(0, 0, temp);
    let minX = temp.x;
    let minY = temp.y;
    let maxX = temp.x;
    let maxY = temp.y;
    
    this.screenToWorld(this.viewportWidth, 0, temp);
    minX = Math.min(minX, temp.x);
    minY = Math.min(minY, temp.y);
    maxX = Math.max(maxX, temp.x);
    maxY = Math.max(maxY, temp.y);
    
    this.screenToWorld(0, this.viewportHeight, temp);
    minX = Math.min(minX, temp.x);
    minY = Math.min(minY, temp.y);
    maxX = Math.max(maxX, temp.x);
    maxY = Math.max(maxY, temp.y);
    
    this.screenToWorld(this.viewportWidth, this.viewportHeight, temp);
    minX = Math.min(minX, temp.x);
    minY = Math.min(minY, temp.y);
    maxX = Math.max(maxX, temp.x);
    maxY = Math.max(maxY, temp.y);
    
    return { minX, minY, maxX, maxY };
  }
  
  /**
   * Check if a world-space AABB is visible
   */
  isVisible(minX: number, minY: number, maxX: number, maxY: number): boolean {
    const bounds = this.getVisibleBounds();
    return !(maxX < bounds.minX || minX > bounds.maxX ||
             maxY < bounds.minY || minY > bounds.maxY);
  }
  
  /**
   * Update cached matrices
   */
  private updateMatrices(): void {
    if (!this.dirty) return;
    
    // Build view matrix: translate then scale (zoom) then rotate
    this.viewMatrix.setIdentity();
    
    // Apply rotation around origin
    if (this.rotation !== 0) {
      this.viewMatrix.rotateZ(-this.rotation);
    }
    
    // Apply zoom
    this.viewMatrix.scale(this.zoom, this.zoom, 1);
    
    // Apply translation (camera position is negated)
    this.viewMatrix.translate(-this.x, -this.y, 0);
    
    // Build orthographic projection matrix
    // Map from world space to clip space (-1 to 1)
    const halfWidth = this.viewportWidth / 2;
    const halfHeight = this.viewportHeight / 2;
    
    Mat4.ortho(
      -halfWidth, halfWidth,      // left, right
      -halfHeight, halfHeight,    // bottom, top
      -1, 1,                      // near, far
      this.projectionMatrix
    );
    
    // Combine view and projection
    Mat4.multiply(this.projectionMatrix, this.viewMatrix, this.viewProjectionMatrix);
    
    // Calculate inverse for screen-to-world transform
    this.inverseViewProjection.copy(this.viewProjectionMatrix).invert();
    
    this.dirty = false;
  }
  
  /**
   * Mark matrices as needing update
   */
  invalidate(): void {
    this.dirty = true;
  }
  
  /**
   * Clone this camera
   */
  clone(): Camera2D {
    const cam = new Camera2D(this.viewportWidth, this.viewportHeight);
    cam.x = this.x;
    cam.y = this.y;
    cam.zoom = this.zoom;
    cam.rotation = this.rotation;
    return cam;
  }
}
